import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { GetUsersMe200Response } from '@src/api/generated/user-api'
import { PATHS } from '@src/consts/index'
import { Tfa } from '@src/features/invoke/redirect/web/page/invoke-redirect-web-main/type'
import { useSignout } from '@src/hooks/queries'
import { getMailAddress, getSmsPhoneNumber, getUser } from '@src/utils'
import { OAuthResponseType } from '@src/utils/oauth'
import { setOAuthClientParams } from '@src/utils/oauth/oauth'

import { entrancePathNames } from './entrance-path-names'
import { notAllowedPathNames } from './not-allowed-path-names'
import { tfaPathNames } from './tfa-path-names'

const redirectToNuxtPath = async (path: string) => {
  const { IS_STORYBOOK } = getConfig()
  // This is necessary to avoid REPLACEMENT when testing redirections in the storybook.
  if (IS_STORYBOOK) {
    history.replaceState('', '', path)
    return
  }
  await window.location.replace(path)
}

export function homePagePath(user: GetUsersMe200Response): Promise<void> {
  if (
    (user.isOrganizationAdmin || user.isViewer || user.isBookkeeper) &&
    user.hasCardContract
  ) {
    return redirectToNuxtPath(PATHS.CARDS)
  }

  if (
    (user.isOrganizationAdmin || user.isViewer || user.isBookkeeper) &&
    user.hasPayContract
  ) {
    return redirectToNuxtPath(PATHS.PAYMENTS)
  }

  if (user.isGeneralUser && user.hasCardContract) {
    return redirectToNuxtPath(PATHS.USER_CARDS)
  }

  return redirectToNuxtPath('/')
}

const isFreshdeskRedirect = (
  router: ReturnType<typeof useRouter>,
  currentPathName: string
) => {
  return (
    router.query.refererService === 'freshdesk' &&
    currentPathName === PATHS.SIGNIN
  )
}

const isOauthRedirect = (
  router: ReturnType<typeof useRouter>,
  currentPathName: string
) => {
  return (
    !!router.query.login_challenge &&
    typeof router.query.login_challenge === 'string' &&
    currentPathName === PATHS.SIGNIN
  )
}
export const useRedirectRules = () => {
  const router = useRouter()
  const { mutate } = useSignout()

  const redirectIfNotAccess = async () => {
    const user = getUser()

    // ■ Perform type guard
    if (!router) return
    const currentPathName = router.pathname

    // ■ Persist client information for oauth
    const { ENABLE_OAUTH } = getConfig().publicRuntimeConfig
    if (currentPathName === PATHS.SIGNIN && ENABLE_OAUTH === 'true') {
      setOAuthClientQueryParams()
    }

    // ■ Check if the user is not logged in but accessing the auhorized pages
    if (!user) {
      if (entrancePathNames.includes(currentPathName)) return
      if (canAccessTfaPages(currentPathName)) return

      const backUrl = getBackUrl()
      mutate({ backUrl, isSessionTimeout: false })
      return
    }

    // ■ Check if the user is logged in but accessing the entrance pages
    if (
      entrancePathNames.includes(currentPathName) ||
      tfaPathNames.includes(currentPathName)
    ) {
      // ■ Check when redirected from freshdesk
      // stay on the entrace pages to continue sso process.
      if (isFreshdeskRedirect(router, currentPathName)) return

      // ■ Check when redirected from oauth
      // stay on the entrace pages to continue oauth process.
      if (isOauthRedirect(router, currentPathName)) return

      // Redirect to the home page.
      await homePagePath(user)
      return
    }

    // ■ Check if it is a error page
    // TODO: I just copied the Nuxt code and will need to rewrite it later.
    // if (isErrorPage(path)) return

    // ■ Check card contract and pay contract status
    if (!user.hasCardContract && !user.hasPayContract) {
      const backUrl = getBackUrl()
      mutate({ backUrl, isSessionTimeout: false })
      return
    }

    // ■ Check password registration status
    // TODO: When SAML information can be retrieved by user-api, add a condition whether SAML is enforced or not.
    if (user.isTempPassword) {
      if (!currentPathName.startsWith(PATHS.REGISTER_PASSWORD)) {
        return redirectToNuxtPath(PATHS.REGISTER_PASSWORD)
      }
      return
    }

    // ■ Access control per role
    if (user.isOrganizationAdmin) {
      return
    }

    if (user.isGeneralUser) {
      const fullPath = router.asPath
      // FIXME: This is a temporary redirect for only transactions.
      // Basically, this kind of redirect should NOT be written in this file.
      const match = matchCardsTransactionsPattern(fullPath)
      if (match.isMatched) {
        const [userAccountId, transactionId, toYearMonth] = match.matchedParams
        await redirectToNuxtPath(
          PATHS.USER_CARDS_TRANSACTIONS_DETAILS({
            userAccountId,
            transactionId,
            toYearMonth,
          })
        )
        return
      }

      if (
        notAllowedPathNames.generalUser.some((v) =>
          currentPathName.startsWith(v)
        )
      ) {
        await redirectToNuxtPath(PATHS.USER_CARDS)
        return
      }
    }
    if (user.isBookkeeper) {
      if (
        notAllowedPathNames.bookkeeper.some((v) =>
          currentPathName.startsWith(v)
        )
      ) {
        await redirectToNuxtPath(PATHS.CARDS)
        return
      }
    }
    if (user.isViewer) {
      if (
        notAllowedPathNames.viewer.some((v) => currentPathName.startsWith(v))
      ) {
        await redirectToNuxtPath(PATHS.CARDS)
        return
      }
      return
    }
  }

  function setOAuthClientQueryParams() {
    setOAuthClientParams({
      clientId: router.query.client_id as string,
      responseType: router.query.response_type as OAuthResponseType,
      redirectUrl: router.query.redirect_url as string,
      state: router.query.state as string,
    })
  }

  function canAccessTfaPages(currentPathName: string): boolean {
    //TODO: use zod to validate query
    const tfaType = router.query.tfa as Tfa
    const tfaToken = router.query.tfaToken as string
    const mailAddress =
      typeof router.query.email === 'string'
        ? router.query.email
        : getMailAddress()
    const smsPhoneNumberQuery = router.query.smsPhoneNumber
    const smsPhoneNumber = smsPhoneNumberQuery ?? getSmsPhoneNumber()

    if (currentPathName === PATHS.SIGNIN_TFA_APP) {
      return !!mailAddress || (tfaType === 'app' && !!tfaToken)
    }
    if (currentPathName === PATHS.SIGNIN_TFA_SMS) {
      return !!smsPhoneNumber || (tfaType === 'sms' && !!tfaToken)
    }
    if (currentPathName === PATHS.SIGNIN_TFA_RECOVERY_CODE) {
      return !!mailAddress || !!smsPhoneNumber
    }
    return false
  }

  function matchCardsTransactionsPattern(path: string):
    | {
        isMatched: true
        matchedParams: [string, string, string?]
      }
    | {
        isMatched: false
      } {
    // NOTE: Please be sure to ensure that it works with or without a slash before ?yearMonth= when you fix it below.
    const pattern =
      /\/cards\/users\/(.+)\/transactions\/([^/?]+)(\/?\?yearMonth=(.+))?/
    const match = path.match(pattern)
    if (match) {
      const [
        ,
        userAccountId,
        transactionId /* ?yearMonth=xxxxxx */,
        ,
        toYearMonth,
      ] = match
      return {
        isMatched: true,
        matchedParams: [userAccountId, transactionId, toYearMonth],
      }
    }
    return { isMatched: false }
  }

  function getBackUrl(): string {
    // FIXME: Please extend or modify the code below to include other paths as well.
    const backUrl = router.pathname.startsWith(PATHS.MESSAGES)
      ? router.asPath // To get exact path including path parameters when the path is dynamic since router.pathname returns like /path/[id]
      : router.pathname + window.location.search + window.location.hash
    return encodeURIComponent(backUrl)
  }

  // The entire application uses useEffect to run on the client due to SSR
  useEffect(() => {
    redirectIfNotAccess()
    // It is set to disable because you only want to run it the first time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
